/**
 * @module use-intersection-observer
 * React hook that reports when one or more DOM elements enter or leave the
 * viewport (or a custom scroll container).
 *
 * Attach the returned `ref` to any number of elements to receive a callback
 * the moment each element becomes visible or invisible. Perfect for
 * lazy‑loading images, triggering entrance/exit animations, or logging
 * analytics events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type IntersectionObserverOptions = {
  /** Element that scrolls (null ⇒ use the browser viewport). */
  root?: HTMLElement | null
  /** Extra area (px / %) around the viewport that counts as "visible". */
  rootMargin?: string // default: '0px'
  /** Intersection threshold (0–1). Default: 0. */
  threshold?: number
  /** Delay in ms before calling `onEnter`. */
  onEnterDelay?: number
  /** Called the first time a target enters view. Receives the target element. */
  onEnter: (element: HTMLElement) => void
  /** Called the first time a target leaves view. Receives the target element. */
  onLeave?: (element: HTMLElement) => void
}

type IntersectionObserverHook = {
  elementRef: (node: HTMLElement | null) => void
}

/**
 * Returns a ref callback that invokes `onEnter` or `onLeave` whenever any
 * attached element crosses the intersection threshold of the specified `root`
 * (defaults to the browser viewport).
 *
 * The observer is created lazily and shared across all nodes attached during
 * the lifetime of the calling component, so you can safely reuse the same ref.
 *
 * @param options.root        Optional scroll container to observe against.
 * @param options.rootMargin  Extra margin around the root, identical to the
 *                            IntersectionObserver `rootMargin` parameter.
 * @param options.threshold   Intersection threshold (0–1). Default `0`.
 * @param options.onEnter     Callback fired each time a node becomes visible.
 * @param options.onLeave     Callback fired each time a node becomes invisible.
 *
 * @returns {React.RefCallback<HTMLElement>} Reusable ref to attach to one or
 *          many DOM elements.
 *
 * @example
 * const ref = useIntersectionObserver({
 *   onEnter: el => console.log('Entered:', el),
 *   onLeave: el => console.log('Left:', el),
 * });
 * return <img ref={ref} src="…" />;
 */
export function useIntersectionObserver({
  root,
  rootMargin = '0px',
  threshold = 0,
  onEnterDelay = 150,
  onEnter,
  onLeave
}: IntersectionObserverOptions): IntersectionObserverHook {
  // Observer and bookkeeping
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedNodesRef = useRef(new Set<HTMLElement>())
  const enteredElementsRef = useRef(new Set<Element>())
  const cancelMapRef = useRef(new Map<Element, () => void>())

  // Callback refs to avoid recreating observer when handlers change
  const onEnterRef = useRef(onEnter)
  const onLeaveRef = useRef(onLeave)
  const onEnterDelayRef = useRef(onEnterDelay)

  useEffect(() => {
    onEnterRef.current = onEnter
  }, [onEnter])

  useEffect(() => {
    onLeaveRef.current = onLeave
  }, [onLeave])

  useEffect(() => {
    onEnterDelayRef.current = onEnterDelay
  }, [onEnterDelay])

  // Create/recreate observer when governing options change
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target
          const hasEntered = enteredElementsRef.current.has(el)

          if (entry.isIntersecting) {
            if (hasEntered) return
            if (!cancelMapRef.current.has(el)) {
              const { cancel } = runLater(() => {
                enteredElementsRef.current.add(el)
                cancelMapRef.current.delete(el)
                onEnterRef.current(el as HTMLElement)
              }, onEnterDelayRef.current)
              cancelMapRef.current.set(el, cancel)
            }
          } else {
            const cancel = cancelMapRef.current.get(el)
            if (cancel) {
              cancel()
              cancelMapRef.current.delete(el)
            }
            if (hasEntered) {
              enteredElementsRef.current.delete(el)
              onLeaveRef.current?.(el as HTMLElement)
            }
          }
        })
      },
      { root, rootMargin, threshold }
    )

    observerRef.current = observer
    // Re-observe all known nodes with the new observer instance
    observedNodesRef.current.forEach((node) => observer.observe(node))

    return () => {
      observer.disconnect()
    }
  }, [root, rootMargin, threshold])

  // Ref callback – stable identity
  const attach = useCallback((node: HTMLElement | null) => {
    if (!node) return
    observedNodesRef.current.add(node)
    observerRef.current?.observe(node)
  }, [])

  // Final cleanup on unmount: cancel timers and clear bookkeeping
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      cancelMapRef.current.forEach((c) => c())
      cancelMapRef.current.clear()
      enteredElementsRef.current.clear()
      observedNodesRef.current.clear()
    }
  }, [])

  return { elementRef: attach }
}

type ElementsInViewOptions = Pick<
  IntersectionObserverOptions,
  'root' | 'rootMargin' | 'threshold'
> & {
  elementIdentifierDataAttribute: string
}

type ElementsInViewHook = {
  elementRef: (node: HTMLElement | null) => void
  elementIdentifiersInView: string[]
}

export function useElementsInView(options: ElementsInViewOptions): ElementsInViewHook {
  const [elementIdentifiersInView, setElementIdentifiersInView] = useState<string[]>([])

  const intersectionObserverOptions = useMemo(
    () => ({
      root: options.root,
      rootMargin: options.rootMargin,
      threshold: options.threshold,
      onEnter: (element: HTMLElement) => {
        const identifier = element.dataset[options.elementIdentifierDataAttribute]!
        setElementIdentifiersInView((prev) =>
          prev.includes(identifier) ? prev : [...prev, identifier]
        )
      },
      onLeave: (element: HTMLElement) => {
        const identifier = element.dataset[options.elementIdentifierDataAttribute]!
        setElementIdentifiersInView((prev) =>
          prev.includes(identifier) ? prev.filter((id) => id !== identifier) : prev
        )
      }
    }),
    [options.root, options.rootMargin, options.threshold, options.elementIdentifierDataAttribute]
  )

  const { elementRef } = useIntersectionObserver(intersectionObserverOptions)

  return { elementRef, elementIdentifiersInView }
}

/**
 * Schedule `fn` to run once after `delayMs`.
 * Returns a `cancel()` function that prevents it from running.
 *
 * Usage:
 *   const { cancel } = runLater(() => backend.renderPage(42), 150);
 *   …
 *   // if the page leaves view before 150 ms:
 *   cancel();   // the callback becomes a no-op
 */
function runLater(fn: () => void, delayMs: number): { cancel: () => void } {
  const id = setTimeout(fn, delayMs)
  return { cancel: () => clearTimeout(id) }
}
